import React, { useEffect, useState } from 'react';

// Styles & Assets
import styles from './CloseCaseForm.module.css';

// Services
import { getEnvironmentApiUrl } from '../../../services/General';

// Hooks
import { useTranslation } from 'react-i18next';
import useHttp from '../../../hooks/useHttp';

// Components
import InputSelect from '../../UI/InputSelect/InputSelect';

// Other
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { createTheme, ThemeProvider } from '@mui/material/styles';

// Interfaces
import { CloseReason } from '../../../interfaces/Patient';
import { OptionsSelect } from '../../../interfaces/Input';
interface Props {
    endDate: moment.Moment | null;
    changeEndDate: (date: moment.Moment | null) => void;
    closureReason: CloseReason | null;
    changeClosureReason: (id: CloseReason | null) => void;
    handleContinue: () => void;
}

const CloseCaseForm: React.FC<Props> = ({
    endDate,
    changeEndDate,
    closureReason,
    changeClosureReason,
    handleContinue
}) => {

    const { t } = useTranslation();

    const [closeReasons, setCloseReasons] = useState<CloseReason[]>([]);
    const { sendRequest } = useHttp<CloseReason[]>();

    useEffect(() => {
        sendRequest({
            url: getEnvironmentApiUrl() + 'case/closure_reasons?bc_home=true',
            method: 'GET',
        }, (data) => setCloseReasons(data));
    }, [sendRequest]);

    const options = closeReasons.map((reason) => ({ id: reason.id, name: reason.name }));

    const newTheme = createTheme({
        components: {
            MuiInputBase: {
                styleOverrides: {
                    input: {
                        fontSize: '15px',
                        padding: '12px 0 12px 12px !important',
                        fontFamily: 'MontserratRegular'
                    },
                },
            },
            MuiInputAdornment: {
                styleOverrides: {
                    root: { margin: '0' }
                }
            }
        }
    });

    const handleChangeDate = (date: moment.Moment | null) => {
        changeEndDate(date);
    }

    const handleChangeClosure = (o: OptionsSelect) => {
        const reason = closeReasons.find((r) => r.id === o.id);
        changeClosureReason(reason ?? null);
    }

    const handleConfirm = () => {
        handleContinue();
    }

    return (
        <div className='dialog'>
            <h1 className='dialogTitle' style={{ marginBottom: '18px' }}>
                {t('close_case')}
            </h1>
            <div className={styles.inputContainer}>
                <p className={styles.label}>
                    {t('case_closure_date')}
                </p>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                    <ThemeProvider theme={newTheme}>
                        <DatePicker
                            className={styles.datePicker}
                            defaultValue={endDate}
                            format='DD-MM-YYYY'
                            onChange={handleChangeDate}
                        />
                    </ThemeProvider>
                </LocalizationProvider>
            </div>
            <p className={styles.label}>
                {t('select_type_closure')}
            </p>
            <InputSelect
                label=''
                simpleStyles
                value={closureReason?.name ?? ''}
                options={options}
                onChange={handleChangeClosure}
                width={300}
            />
            <div className={styles.buttonContainer}>
                <button
                    onClick={handleConfirm}
                    className={`button ${styles.buttonConfirm}`}
                    disabled={endDate === null || !endDate.isValid() || closureReason === null}
                >
                    {t('continue')}
                </button>
            </div>
        </div>
    );
};

export default CloseCaseForm;